<script lang="ts" setup>
import SeLink from '@/src/components/Link.vue';
import { usePromoBanner } from '@/src/composables/usePromoBanner';
import SeAdvisorAccuracy from '@/src/modules/advisor/components/shared/advisor-accuracy.vue';
import SeAdvisorCardChatOptions from '@/src/modules/advisor/components/shared/advisor-card-chat-options.vue';
import SeAdvisorCardDiscountBanner from '@/src/modules/advisor/components/shared/advisor-card-discount-banner.vue';
import SeAdvisorCategories from '@/src/modules/advisor/components/shared/advisor-categories.vue';
import SeAdvisorGeneralPrice from '@/src/modules/advisor/components/shared/advisor-general-price.vue';
import SeAdvisorOfflineOptions from '@/src/modules/advisor/components/shared/advisor-offline-options.vue';
import SeAdvisorStatus from '@/src/modules/advisor/components/shared/advisor-status.vue';
import SeAvatar from '@/src/ui-kit/avatar/Avatar.vue';
import SeCard from '@/src/ui-kit/card/Card.vue';
import SeIcon from '@/src/ui-kit/icon/Icon.vue';
import { computed } from 'vue';
import SeButton from '@/src/ui-kit/button/Button.vue';
import { theme } from '@/theme';

const props = withDefaults(
  defineProps<{
    advisor: any;
    categoriesVisible?: boolean;
  }>(),
  {
    categoriesVisible: false,
  }
);
const { siteActive } = theme.general;
const { isPromoActive } = usePromoBanner();
const isOnline = computed(() => props.advisor.status === 'online' && siteActive);
</script>

<script lang="ts">
export default {
  name: 'SeAdvisorCardSmallImage',
};
</script>
<template>
  <se-link :href="`/advisor/${advisor.url}`" class="advisor-card block link text-black no-underline h-full w-full">
    <se-card v-if="advisor" class="h-full flex flex-col transition overflow-hidden hover:shadow-large">
      <div class="flex gap-16 relative py-24 px-16">
        <se-avatar
          :id="advisor.id"
          :name="advisor.name"
          :src="advisor.avatar"
          :size="120"
          class="!rounded-[12px] shrink-0"
        />
        <div class="flex flex-col">
          <h3 class="text-h-m font-bold !mb-8 line-clamp-1">
            {{ advisor.name }}
          </h3>
          <se-advisor-accuracy :is-one-lined="false" :advisor="advisor" :reviews-count="advisor.reviews_count" />
          <div class="flex z-10 mt-8">
            <se-advisor-status :advisor="advisor" />
          </div>
        </div>
      </div>
      <div class="px-16 h-full flex flex-col justify-between">
        <div class="text-h-m text-neutral-80 mb-16 line-clamp-3">
          <span v-if="advisor.short_description">{{ advisor.short_description }}</span>
          <span v-else>This advisor doesn’t have any description yet</span>
        </div>
        <se-advisor-categories v-if="categoriesVisible" :advisor="advisor" />
        <div class="flex justify-center items-center gap-2 font-medium text-[12px]">
          <p>View Full Profile</p>
          <se-icon name="chevron-right" :size="14" class="font-medium" />
        </div>
      </div>
      <div
        :class="{ 'bg-neutral-10 md:mt-24 mt-20': !categoriesVisible }"
        class="px-[10px] 3xs:px-16 py-20 md:py-24 flex flex-col justify-between"
      >
        <template v-if="isOnline">
          <se-advisor-card-discount-banner v-if="isPromoActive" :advisor="advisor" class="mb-16" />
          <div
            v-else-if="advisor.free_minutes_left || advisor.discount"
            class="bg-primary-10 rounded-[8px] text-neutral-100 text-center p-2 mb-16 font-medium"
          >
            <span v-if="advisor.free_minutes_left">Start {{ advisor.free_minutes_left }} min Free Chat</span>
            <span v-else>Start Session</span>
            <span v-if="advisor.discount">&nbsp;&nbsp;•&nbsp;&nbsp;{{ advisor.discount }}% OFF</span>
          </div>
          <se-advisor-card-chat-options :advisor="advisor" />
        </template>
        <template v-else>
          <div class="bg-neutral-0 rounded-8 flex items-center justify-between p-12 mb-16">
            <div class="flex items-center gap-[6px]">
              <div v-if="advisor.video_enabled">
                <se-icon name="video" :size="24" :class="isOnline ? 'text-primary-100' : 'text-neutral-50'" />
              </div>
              <div v-if="advisor.audio_enabled">
                <se-icon name="phone" :size="20" :class="isOnline ? 'text-feature-100' : 'text-neutral-50'" />
              </div>
              <div v-if="advisor.text_enabled">
                <se-icon name="message-circle" :size="20" :class="isOnline ? 'text-love-100' : 'text-neutral-50'" />
              </div>
            </div>
            <div class="flex justify-right items-center">
              <se-advisor-general-price :advisor="advisor" />
            </div>
          </div>
          <div class="flex items-center gap-8">
            <se-advisor-offline-options v-if="siteActive" :advisor="props.advisor" />
            <se-link v-else class="w-full" href="https://www.seeer.com">
              <se-button class="w-full">Go To Seeer.com</se-button>
            </se-link>
          </div>
        </template>
      </div>
    </se-card>
  </se-link>
</template>
